<script setup>
import { ref, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'

import { useNotify } from '@/composables/notify'
import { useStepper } from '@/composables/stepper'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useArchive } from '@/composables/archive'
import { useClone } from '@/composables/object'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import CameraGalleryInput from '@/components/shared/CameraGalleryInput.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  changes: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  tags: {
    type: Array,
    required: false,
    default: () => [],
  },
  availability: {
    type: Array,
    required: false,
    default: () => [],
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { step, nextStep, prevStep, markAttempted } = useStepper()
const { notifySuccess, notifyError } = useNotify()

const planningStore = usePlanningStore()

const form = ref(null)
const cameraGalleryInput = ref(null)
const editItem = ref({})

const { isUpdated, isUpdating, setOriginal } = useIsUpdated(editItem)

onMounted(async () => {
  if (props.modelValue?.id) {
    editItem.value = useClone((await planningStore.fetchCompanyByIdAsync(props.modelValue.id)).data)
    setOriginal(editItem.value)
    Object.assign(editItem.value, props.changes)
  } else {
    editItem.value = { ...useClone(props.modelValue), code: String(await planningStore.fetchNextCompanyIdAsync()) }
    setOriginal(editItem.value)
  }
})


const { archiveWarning, archiveAsync, onRowRemoving: onDelete } = useArchive({
  onArchive: async(id) => { await planningStore.archiveCompanyAsync(id) },
  onDelete: async(id, config) => { 
    await planningStore.deleteCompanyAsync(id, config) 
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    emit('delete')
  },
})

const onUpdate = async() => {
  isUpdating.value = true
  const updateItem = { ...editItem.value }

  if (!editItem.value.avatar_file?.payload) {
    delete updateItem.avatar_file
  }
  
  try {
    const saved = await planningStore.upsertCompanyAsync(updateItem)
    updateItem.id = updateItem.id || saved.data.id
    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    emit('update', updateItem)
  } catch(error)  {
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
  } finally {
    isUpdating.value = false
  }
}

const showDeleteDialog = ref(false)
const onConfirmDelete = () => { showDeleteDialog.value = true }

const emit = defineEmits(['update', 'cancel', 'delete'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')

</script>
<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => {
        await archiveAsync(archiveWarning.item.id, false)
        emit('update')
      }"
    />
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete({ id: editItem.id })"
    />
    <navigate-back
      :text="editItem?.id ? i18n.t('common.editCompany') : i18n.t('common.addCompany')"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'dialog-card-content': editItem?.id }">
      <q-card-section class="q-px-sm q-pt-none">
        <q-form ref="form">
          <q-stepper v-model="step" class="q-py-none">
            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              icon="mdi-folder-plus"
              :done="!!editItem.id || step > 1"
              :header-nav="!!editItem.id || step > 1"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.code"
                  :class="isMobile ? 'col-4' : 'col-2'"
                  :label="i18n.t('common.code')"
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-8' : 'col-6'"
                  :label="i18n.t('common.name')"
                  autofocus
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <q-input
                  v-model="editItem.vat_number"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.vatNumber')"
                />
                <q-input
                  v-model="editItem.address"
                  class="col-12"
                  :label="i18n.t('common.address')"
                />
                <q-checkbox
                  v-model="editItem.is_client"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.client')"
                  color="primary"
                />
                <q-checkbox
                  v-model="editItem.is_rental"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.rental')"
                  color="primary"
                />
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(1); form.validate().then((success) => (success ? nextStep() : null)) }"
              />
            </q-step>
            <q-step
              :name="2"
              :title="i18n.t('common.picture')"
              icon="mdi-camera"
              :done="!!editItem.id || step > 2"
              :header-nav="!!editItem.id || step > 2"
            >
              <div class="column items-center q-py-md" style="min-height: 10rem;">
                <CameraGalleryInput ref="cameraGalleryInput" :edit-item="editItem" />
                <q-btn
                  v-if="!editItem.avatar_file && !editItem.avatar_url"
                  icon="mdi-camera"
                  size="2rem"
                  round
                  @click="cameraGalleryInput.onActivateCamera()"
                />
                <q-avatar
                  v-else
                  size="10rem"
                  class="cursor-pointer"
                  @click="cameraGalleryInput.onActivateCamera()"
                >
                  <q-img
                    loading="lazy"
                    fit="contain"
                    :src="editItem.avatar_file?.payload || editItem.avatar_file || editItem.avatar_url"
                  >
                    <div class="absolute-full text-subtitle2 flex flex-center image-camera">
                      <q-icon name="mdi-camera" size="2rem" />
                    </div>
                  </q-img>
                </q-avatar>
              </div>
              <stepper-navigation :back="prevStep" :continue="nextStep" />
            </q-step>
            <q-step
              :name="3"
              :title="`${i18n.t('common.detail', 2)}`"
              icon="mdi-information-outline"
              :done="!!editItem.id || step > 3"
              :header-nav="!!editItem.id || step > 3"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.description"
                  class="col-12"
                  :label="i18n.t('common.description')"
                  autogrow
                />
              </div>
              <stepper-navigation
                :back="prevStep"
                :finish="editItem.id ? null : () => form.validate().then(success => success && onUpdate()) "
                :finish-label="i18n.t('common.addCompany')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="onConfirmDelete"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :disable="editItem.name === ''"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="onUpdate(editItem.id)"
      />
    </q-card-actions>
  </q-card>
</template>
<style scoped>
</style>
